import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';

export const LeadManagement = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [deletingLead, setDeletingLead] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const backendUrl = API_BASE;

  // Helper function to get display name from lead
  const getLeadDisplayName = (lead) => {
    // For legacy forms: use full_name
    if (lead.full_name) {
      return lead.full_name;
    }
    
    // For dynamic forms: check common field names
    if (lead.form_type === 'dynamic' && lead.data) {
      return lead.data.student_name || 
             lead.data.full_name || 
             lead.data.name || 
             lead.data.student_email ||
             lead.data.email ||
             'No contact info';
    }
    
    // Fallback
    return lead.email || 'No contact info';
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [leads, filterType, filterStatus, searchQuery]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/forms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch leads');

      const data = await response.json();
      setLeads(data.forms || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...leads];

    if (filterType !== 'all') {
      filtered = filtered.filter(lead => lead.form_type === filterType);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(lead => (lead.lead_status || lead.status || 'new') === filterStatus);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(lead => {
        const displayName = getLeadDisplayName(lead);
        const formSlug = lead.form_slug || '';
        
        // Search in name, email, form_slug, and phone
        if (displayName.toLowerCase().includes(query) ||
            (lead.email || '').toLowerCase().includes(query) ||
            formSlug.toLowerCase().includes(query) ||
            (lead.contact_number || '').includes(query)) {
          return true;
        }
        
        // Search in dynamic form data
        if (lead.form_type === 'dynamic' && lead.data) {
          return Object.values(lead.data).some(value => 
            String(value).toLowerCase().includes(query)
          );
        }
        
        return false;
      });
    }

    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setFilteredLeads(filtered);
  };

  const getFormTypeLabel = (type) => {
    const labels = {
      'general_inquiry': 'General Inquiry',
      'appointment_booking': 'Appointment',
      'campus_tour': 'Campus Tour',
      'dynamic': 'Custom Form'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const statusValue = status || 'new';
    const styles = {
      'new': 'bg-blue-50 text-blue-700 border border-blue-200',
      'contacted': 'bg-amber-50 text-amber-700 border border-amber-200',
      'closed': 'bg-emerald-50 text-emerald-700 border border-emerald-200'
    };
    return styles[statusValue] || 'bg-gray-50 text-gray-700 border border-gray-200';
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [leadId]: true }));

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/forms/${leadId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ lead_status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');

      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(lead => 
          lead.id === leadId ? { ...lead, lead_status: newStatus, status: newStatus } : lead
        )
      );
    } catch (err) {
      console.error(err);
      alert('Failed to update status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [leadId]: false }));
    }
  };

  const deleteLead = async (leadId) => {
    setDeletingLead(leadId);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/forms/${leadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete lead');

      // Remove from local state
      setLeads(prevLeads => prevLeads.filter(lead => lead.id !== leadId));
      setShowDeleteModal(false);
      setSelectedLead(null);
    } catch (err) {
      console.error(err);
      alert('Failed to delete lead');
    } finally {
      setDeletingLead(null);
    }
  };

  const exportToExcel = () => {
    const headers = ['ID', 'Name', 'Type', 'Contact', 'Email', 'Status', 'Date'];
    const rows = filteredLeads.map(lead => [
      lead.id,
      getLeadDisplayName(lead),
      getFormTypeLabel(lead.form_type),
      lead.contact_number || lead.whatsapp_number || lead.data?.phone_number || '-',
      lead.email || lead.data?.student_email || lead.data?.email || '-',
      lead.lead_status || lead.status || 'new',
      new Date(lead.created_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `leads_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading leads...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-[1280px]">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#111827]" data-testid="page-title">Lead Management</h1>
            <p className="text-[#6B7280] text-sm mt-1">Track and manage all form submissions</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportToExcel}
              disabled={filteredLeads.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E5E7EB] text-[#374151] rounded-lg font-medium text-sm hover:bg-[#F9FAFB] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="export-excel"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-[#1E40AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{leads.length}</p>
                <p className="text-xs text-[#6B7280]">Total Leads</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{leads.filter(l => (l.lead_status || l.status || 'new') === 'new').length}</p>
                <p className="text-xs text-[#6B7280]">New</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{leads.filter(l => (l.lead_status || l.status) === 'contacted').length}</p>
                <p className="text-xs text-[#6B7280]">Contacted</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-[#111827]">{leads.filter(l => (l.lead_status || l.status) === 'closed').length}</p>
                <p className="text-xs text-[#6B7280]">Closed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent text-sm"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex gap-3">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent text-sm bg-white min-w-[150px]"
                data-testid="filter-type"
              >
                <option value="all">All Types</option>
                <option value="general_inquiry">General Inquiry</option>
                <option value="appointment_booking">Appointment</option>
                <option value="campus_tour">Campus Tour</option>
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2.5 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent text-sm bg-white min-w-[130px]"
                data-testid="filter-status"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="closed">Closed</option>
              </select>

              {(filterType !== 'all' || filterStatus !== 'all' || searchQuery) && (
                <button
                  onClick={() => {
                    setFilterType('all');
                    setFilterStatus('all');
                    setSearchQuery('');
                  }}
                  className="px-4 py-2.5 text-[#F97316] hover:bg-orange-50 rounded-lg font-medium text-sm transition-colors"
                  data-testid="clear-filters"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
            <p className="text-sm text-[#6B7280]">
              Showing <span className="font-semibold text-[#111827]">{filteredLeads.length}</span> of <span className="font-semibold text-[#111827]">{leads.length}</span> leads
            </p>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
          {filteredLeads.length === 0 ? (
            <div className="text-center py-16">
              <svg className="w-16 h-16 mx-auto text-[#E5E7EB] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-[#6B7280] font-medium">No leads found</p>
              <p className="text-sm text-[#9CA3AF] mt-1">Try adjusting your filters or search query</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                    <th className="text-left py-3.5 px-5 text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Lead</th>
                    <th className="text-left py-3.5 px-5 text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Type</th>
                    <th className="text-left py-3.5 px-5 text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Contact</th>
                    <th className="text-left py-3.5 px-5 text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Status</th>
                    <th className="text-left py-3.5 px-5 text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Date</th>
                    <th className="text-right py-3.5 px-5 text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB]">
                  {filteredLeads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="hover:bg-[#F9FAFB] transition-colors"
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <td className="py-4 px-5">
                        <div>
                          <p className="text-sm font-semibold text-[#111827]">
                            {getLeadDisplayName(lead)}
                          </p>
                          <p className="text-xs text-[#6B7280]">
                            {lead.form_type === 'dynamic' ? (
                              lead.data?.student_email || lead.data?.email || lead.email || '-'
                            ) : (
                              lead.email || '-'
                            )}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-[#F3F4F6] text-[#374151]">
                          {lead.form_type === 'dynamic' ? (
                            <>📋 {lead.form_slug || 'Custom Form'}</>
                          ) : (
                            getFormTypeLabel(lead.form_type)
                          )}
                        </span>
                      </td>
                      <td className="py-4 px-5 text-sm text-[#6B7280]">
                        {lead.form_type === 'dynamic' ? (
                          lead.data?.contact_number || lead.data?.phone || '-'
                        ) : (
                          lead.contact_number || lead.whatsapp_number || '-'
                        )}
                      </td>
                      <td className="py-4 px-5" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={lead.lead_status || lead.status || 'new'}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          disabled={updatingStatus[lead.id]}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1E40AF] disabled:opacity-50 ${getStatusBadge(lead.lead_status || lead.status)}`}
                          data-testid={`status-dropdown-${lead.id}`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td className="py-4 px-5 text-sm text-[#6B7280]">
                        {new Date(lead.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/admin/leads/${lead.id}`)}
                            className="p-2 text-[#6B7280] hover:text-[#1E40AF] hover:bg-[#EFF6FF] rounded-lg transition-colors"
                            title="View Details"
                            data-testid={`view-lead-${lead.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedLead(lead);
                              setShowDeleteModal(true);
                            }}
                            className="p-2 text-[#6B7280] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Lead"
                            data-testid={`delete-lead-${lead.id}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6" data-testid="delete-modal">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#111827]">Delete Lead</h3>
                <p className="text-sm text-[#6B7280]">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-[#4B5563] mb-6">
              Are you sure you want to delete the lead from <span className="font-semibold">{getLeadDisplayName(selectedLead)}</span>?
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedLead(null);
                }}
                className="px-4 py-2 text-[#6B7280] hover:bg-[#F3F4F6] rounded-lg font-medium text-sm transition-colors"
                data-testid="cancel-delete"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteLead(selectedLead.id)}
                disabled={deletingLead === selectedLead.id}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
                data-testid="confirm-delete"
              >
                {deletingLead === selectedLead.id ? 'Deleting...' : 'Delete Lead'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default LeadManagement;
